/**
 * useClickOutside Hook
 * Detects clicks outside of a specified element.
 */

import { useEffect, useRef, type RefObject } from 'react';

/**
 * Calls a handler when a click occurs outside the referenced element.
 * @param handler - Function to call on outside click.
 * @returns Ref to attach to the target element.
 */
export const useClickOutside = <T extends HTMLElement = HTMLElement>(
  handler: () => void
): RefObject<T | null> => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handler]);

  return ref;
};

